import { useEffect, useRef, useState, type UIEvent } from "react";
import { blocksToPlainText } from "./chatMessageUtils";
import {
  AI_CHAT_DRAFT,
  AI_CHAT_STATUS_EVENT,
  CHAT_URL,
  COMPACT_URL,
  REGENERATE_URL,
  type AiChatStatus,
} from "./chatConstants";
import {
  addStep,
  announcementFor,
  applyOutcome,
  updateMessage,
  updateMessageBlocks,
  type ChatItem,
  type ChatRequest,
} from "./chatSession";
import {
  buildChatHeaders,
  compactChat,
  loadChatHistory,
  streamChatReply,
  type ChatContext,
  type ChatHistoryEntry,
  type StreamOutcome,
} from "./chatStream";

export type CompactState = { status: "idle" } | { status: "busy" } | { status: "failed"; message: string };

const IDLE_COMPACT: CompactState = { status: "idle" };

const toChatItems = (history: ChatHistoryEntry[]): ChatItem[] => {
  let nextId = Date.now();
  return history.map((entry) => ({ id: nextId++, ...entry }));
};

const rejectionOf = (outcome: StreamOutcome) =>
  outcome.status === "failed" &&
  (outcome.error.code === "chat_limit_reached" ||
    outcome.error.code === "chat_busy" ||
    outcome.error.code === "message_too_long")
    ? outcome
    : null;

export const keepHighest = (previous: ChatContext | null, incoming: ChatContext): ChatContext =>
  previous && !incoming.limitReached && incoming.used < previous.used
    ? { ...previous, limitReached: false }
    : incoming;

const SCROLL_PIN_THRESHOLD_PX = 48;

const PAGE_SCROLLER_SELECTOR = ".layout-page-content";

export const useAiChatSession = ({ active, pageScroll = false }: { active: boolean; pageScroll?: boolean }) => {
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<ChatItem[]>([]);
  const [announcement, setAnnouncement] = useState("");
  const [messageText, setMessageText] = useState<string>(
    () => sessionStorage.getItem(AI_CHAT_DRAFT) ?? ""
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [context, setContext] = useState<ChatContext | null>(null);
  const raiseContext = (incoming: ChatContext) => setContext((previous) => keepHighest(previous, incoming));
  const [limitMessage, setLimitMessage] = useState<string | null>(null);
  const [busyNotice, setBusyNotice] = useState<string | null>(null);
  const [compactState, setCompactState] = useState<CompactState>(IDLE_COMPACT);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);
  const streamAbortRef = useRef<AbortController | null>(null);
  const [editing, setEditing] = useState(false);

  const lastMessage = messages[messages.length - 1];
  const lastUserMessage = [...messages].reverse().find((msg) => msg.role === "user");
  const limitReached = context?.limitReached === true || limitMessage !== null;
  const editableMessageId =
    lastUserMessage && lastMessage?.role === "assistant" && !isSending && !isLoadingHistory && !limitReached
      ? lastUserMessage.id
      : null;
  const getScroller = () =>
    pageScroll ? document.querySelector<HTMLElement>(PAGE_SCROLLER_SELECTOR) : scrollRef.current;

  const scrollToLatest = () => {
    const el = getScroller();
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight });
    pinnedToBottomRef.current = true;
    setShowJumpToLatest(false);
  };

  const updatePinned = (el: HTMLElement) => {
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_PIN_THRESHOLD_PX;
    pinnedToBottomRef.current = atBottom;
    setShowJumpToLatest(!atBottom);
  };

  const handleMessagesScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!pageScroll) updatePinned(event.currentTarget);
  };

  useEffect(() => {
    if (!pageScroll) return;
    const el = document.querySelector<HTMLElement>(PAGE_SCROLLER_SELECTOR);
    if (!el) return;
    const handleScroll = () => updatePinned(el);
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [pageScroll]);

  useEffect(() => {
    if (!active) return;
    if (pinnedToBottomRef.current) {
      const el = getScroller();
      el?.scrollTo({ top: el.scrollHeight });
      setShowJumpToLatest(false);
    } else {
      setShowJumpToLatest(messages.length > 0);
    }
  }, [active, messages]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent<AiChatStatus>(AI_CHAT_STATUS_EVENT, { detail: { replying: isSending } }));
  }, [isSending]);

  useEffect(
    () => () => {
      streamAbortRef.current?.abort();
      window.dispatchEvent(new CustomEvent<AiChatStatus>(AI_CHAT_STATUS_EVENT, { detail: { replying: false } }));
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();

    loadChatHistory(controller.signal, (loaded) => {
      if (!controller.signal.aborted) raiseContext(loaded);
    })
      .then((history) => {
        if (controller.signal.aborted || history.length === 0) return;

        setMessages((current) => (current.length > 0 ? current : toChatItems(history)));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingHistory(false);
      });

    return () => controller.abort();
  }, []);

  const handleChatChanged = (value: string) => {
    sessionStorage.setItem(AI_CHAT_DRAFT, value);
    setMessageText(value);
  };

  const handleNewChat = async () => {
    streamAbortRef.current?.abort();

    try {
      const response = await fetch(CHAT_URL, { method: "DELETE", headers: buildChatHeaders() });
      if (!response.ok) return;

      setMessages([]);
      setEditing(false);
      handleChatChanged("");
      resetLengthState();
    } catch {
      // silently ignore errors
    }
  };

  const resetLengthState = () => {
    setContext(null);
    setLimitMessage(null);
    setBusyNotice(null);
    setCompactState(IDLE_COMPACT);
  };

  const handleCompact = async () => {
    if (isSending || compactState.status === "busy") return;

    setCompactState({ status: "busy" });
    const outcome = await compactChat(COMPACT_URL);
    if (outcome.status === "failed") {
      setCompactState({ status: "failed", message: outcome.error.message });
      return;
    }

    try {
      let reloaded: ChatContext | undefined;
      const history = await loadChatHistory(new AbortController().signal, (loaded) => (reloaded = loaded));
      resetLengthState();
      setMessages(toChatItems(history));
      setContext(reloaded ?? outcome.context ?? null);
      pinnedToBottomRef.current = true;
    } catch {
      setCompactState({ status: "failed", message: "Summarised, but couldn't reload the chat. Refresh the page." });
    }
  };

  const rollbackRejectedSend = (assistantMessageId: number, text: unknown) => {
    setMessages((current) => current.filter((msg) => msg.id !== assistantMessageId && msg.id !== assistantMessageId - 1));
    if (typeof text === "string") handleChatChanged(text);
  };

  const handleStop = () => streamAbortRef.current?.abort();

  const runReply = async (request: ChatRequest, assistantMessageId: number) => {
    const controller = new AbortController();
    streamAbortRef.current = controller;
    setIsSending(true);
    setBusyNotice(null);
    setAnnouncement("Vecta is replying");
    setMessages((current) =>
      updateMessage(current, assistantMessageId, (msg) => ({ ...msg, status: undefined, retryRequest: request }))
    );

    const outcome = await streamChatReply({
      ...request,
      signal: controller.signal,
      onBlocks: (blocks) =>
        setMessages((current) => updateMessageBlocks(current, assistantMessageId, blocks)),
      onPending: (pendingKind) =>
        setMessages((current) =>
          updateMessage(current, assistantMessageId, (msg) => ({
            ...msg,
            progress: { pendingKind, statusLabel: msg.progress?.statusLabel ?? null },
          }))
        ),
      onStatus: (statusLabel) =>
        setMessages((current) =>
          updateMessage(current, assistantMessageId, (msg) => ({
            ...msg,
            progress: { pendingKind: msg.progress?.pendingKind ?? null, statusLabel },
            steps: addStep(msg.steps, statusLabel),
          }))
        ),
      onContext: raiseContext,
    });

    const rejection = rejectionOf(outcome);
    if (rejection?.error.code === "chat_limit_reached") {
      setLimitMessage(rejection.error.message);
      if (rejection.context) raiseContext(rejection.context);
    } else if (rejection) {
      setBusyNotice(rejection.error.message);
    }

    if (rejection && request.url === CHAT_URL) {
      rollbackRejectedSend(assistantMessageId, request.body.message);
    } else {
      setMessages((current) =>
        updateMessage(current, assistantMessageId, (msg) => applyOutcome(msg, outcome))
      );
    }

    if (streamAbortRef.current !== controller) return;

    streamAbortRef.current = null;
    setIsSending(false);
    setAnnouncement(announcementFor(outcome));
  };

  const handleSend = async () => {
    const trimmedMessage = messageText.trim();

    if (!trimmedMessage || isSending) {
      return;
    }

    if (editing) {
      await sendEdit(trimmedMessage);
      return;
    }
    const userMessageId = Date.now();
    const assistantMessageId = userMessageId + 1;
    const now = new Date().toISOString();

    pinnedToBottomRef.current = true;
    handleChatChanged("");

    setMessages((current) => [
      ...current,
      {
        id: userMessageId,
        role: "user",
        blocks: [{ type: "text", content: trimmedMessage }],
        createdAt: now,
      },
      { id: assistantMessageId, role: "assistant", blocks: [] },
    ]);

    await runReply({ url: CHAT_URL, body: { message: trimmedMessage } }, assistantMessageId);
  };

  const restartReply = async (request: ChatRequest, assistantMessageId: number) => {
    pinnedToBottomRef.current = true;
    setMessages((current) =>
      updateMessage(current, assistantMessageId, (msg) => ({
        ...msg,
        blocks: [],
        createdAt: undefined,
        status: undefined,
        steps: undefined,
      }))
    );

    await runReply(request, assistantMessageId);
  };

  const handleEditLast = () => {
    if (!editableMessageId) return;
    const target = messages.find((msg) => msg.id === editableMessageId);
    if (!target) return;

    handleChatChanged(blocksToPlainText(target.blocks));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    handleChatChanged("");
  };

  const sendEdit = async (trimmedMessage: string) => {
    const last = messages[messages.length - 1];
    const lastUser = messages.find((msg) => msg.id === editableMessageId);
    if (!last || last.role !== "assistant" || !lastUser) return;

    setEditing(false);
    handleChatChanged("");
    setMessages((current) =>
      updateMessage(current, lastUser.id, (msg) => ({
        ...msg,
        blocks: [{ type: "text", content: trimmedMessage }],
        createdAt: new Date().toISOString(),
      }))
    );

    await restartReply({ url: REGENERATE_URL, body: { message: trimmedMessage } }, last.id);
  };

  const handleRegenerate = async () => {
    if (isSending) return;

    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;

    await restartReply({ url: REGENERATE_URL, body: {} }, last.id);
  };

  const handleRetry = async () => {
    if (isSending) return;

    const last = messages[messages.length - 1];
    if (!last?.retryRequest) return;

    await restartReply(last.retryRequest, last.id);
  };

  return {
    messages,
    hasMessages: messages.length > 0,
    isSending,
    isLoadingHistory,
    announcement,
    messageText,
    scrollRef,
    showJumpToLatest,
    scrollToLatest,
    handleMessagesScroll,
    handleChatChanged,
    handleNewChat,
    handleStop,
    handleSend,
    handleRegenerate,
    handleRetry,
    handleCompact,
    editing,
    editableMessageId,
    handleEditLast,
    cancelEdit,
    length: {
      context,
      limitReached,
      limitMessage,
      busyNotice,
      compactState,
    },
  };
};
